import { ChangeDetectionStrategy, Component, OnInit, signal, inject } from '@angular/core';
import {AsyncPipe} from '@angular/common';
import {LettersService} from '../../services/letters.service';
import {MemberStateService} from '../../services/member-state.service';
import {FileService} from '../../services/file.service';
import {FormatService} from '../../services/format.service';
import {UserContextService} from '../../services/user-context.service';
import {AnalyticsService} from '../../services/analytics.service';
import {catchError, concatMap, finalize, take, tap} from 'rxjs/operators';
import {TaxForm} from '../../model/tax-form';
import {EMPTY, Observable} from 'rxjs';
import {ViewState, toViewState} from '../../model/view-state';
import {LocalizedText} from '../../model/localized-text';
import { IntroductionComponent } from '../shared/introduction/introduction.component';
import { AlertComponent } from '../shared/alert/alert.component';
import { LoaderComponent } from '../shared/loader/loader.component';
import { TranslatePipe } from '@ngx-translate/core';


@Component({
    selector: 'myabvv-tax-form',
    standalone: true,
    templateUrl: './tax-form.component.html',
    styleUrls: ['./tax-form.component.css'],
    imports: [IntroductionComponent, AlertComponent, LoaderComponent, TranslatePipe, AsyncPipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaxFormComponent implements OnInit {
  private lettersService = inject(LettersService);
  private memberStateService = inject(MemberStateService);
  private fileService = inject(FileService);
  private formatService = inject(FormatService);
  private userContext = inject(UserContextService);
  private analyticsService = inject(AnalyticsService);

  state$: Observable<ViewState<TaxForm[]>>;
  isDownloading = signal(false);
  downloadError = signal(false);

  ngOnInit() {
    const isInternalUser = this.userContext.isInternalUser();
    const year = this.getYearForTaxForm();

    // Use /me endpoint for external users, /{inss} for internal users
    const source$ = isInternalUser
      ? this.memberStateService.getMember().pipe(
          take(1),
          concatMap(member => this.lettersService.getTaxFormList(member.personalInformation.inss, year))
        )
      : this.lettersService.getMyTaxFormList(year);

    this.state$ = source$.pipe(toViewState(err => err.status !== 404));
  }

  getYearForTaxForm(): string {
    const datum = new Date();
    if (datum.getMonth() > 2 || (datum.getMonth() === 2 && datum.getDate() >= 31)) {
      return (datum.getFullYear() - 1).toString();
    } else {
      return (datum.getFullYear() - 2).toString();
    }
  }

  downloadFile(taxFormId: string) {
    this.isDownloading.set(true);
    const isInternalUser = this.userContext.isInternalUser();
    const year = this.getYearForTaxForm();

    this.analyticsService.trackEvent('download_tax_form', { year: year });

    // Use /me endpoint for external users, /{inss} for internal users
    const download$ = isInternalUser
      ? this.memberStateService.getMember().pipe(
          take(1),
          concatMap(member => this.lettersService.getTaxForm(member.personalInformation.inss, taxFormId, year))
        )
      : this.lettersService.getMyTaxForm(taxFormId, year);

    download$.pipe(
      tap(res => {
        this.fileService.downloadFile(res);
      }),
      catchError(() => {
        this.downloadError.set(true);
        this.analyticsService.trackEvent('download_failed', { type: 'tax_form', year: year });
        return EMPTY;
      }),
      finalize(() => {
        this.isDownloading.set(false);
      })
    ).subscribe();
  }

  getTranslatedData(array: LocalizedText[]): string {
    return this.formatService.getTranslatedData(array);
  }

}
